/**
 * Knowledge in memory (docs/design/memory-knowledge.md, 2 Nov 2026): what people and agents actually use — decisions with
 * their why, conventions, architecture facts, how-tos, glossary terms, summaries and answered questions — distilled,
 * deduplicated by a stable key, attributed, dated, labelled and superseded when it changes. A knowledge item is a memory
 * item of kind "knowledge" (projectMemory.ts), scope "knowledge", so labels, entitlement, search, vectors, history and
 * revisions apply to it unchanged; the raw graph (nodes, edges, communities) stays as the index it links to.
 *
 *   POST /projects/v1/:id/memory/knowledge            { items: KnowledgeInput[] ≤ 200 } → KnowledgeWriteResult
 *   GET  /projects/v1/:id/memory/knowledge?type=&rootId=&q=&superseded=1&limit=  → KnowledgeList
 *   GET  /projects/v1/:id/memory/knowledge/:iid       → KnowledgeDetail (the item, what it links to, what it replaced)
 *   POST /projects/v1/:id/memory/knowledge/tidy       contributors: run the home's own pass now → KnowledgeTidyResult
 *
 * The organisation's own index has the same routes under /memory/v1/knowledge. `…/memory/query` answers with the best
 * current knowledge first, then the rest; a superseded or retired item, and a note an answer was distilled from, is
 * never an answer.
 *
 * Who writes it: the workbench distils with its own model (decisions, conventions, architecture, how-tos, glossary)
 * and sends producer "workbench"; a person may write any type; the home's pass needs no model and writes only
 * "summary" and "answer" (producer "aioe"). Mirrored byte for byte by the workbench's shared/schemas/memoryKnowledge.ts.
 */
import { z } from "zod";
import { compartmentIdSchema, sensitivitySchema } from "./memory.js";
export const MEMORY_KNOWLEDGE_SCHEMA_VERSION = 1;
/** The memory item kind knowledge is kept as. */
export const KNOWLEDGE_ITEM_KIND = "knowledge";
export const KNOWLEDGE_TYPES = ["decision", "convention", "architecture", "howto", "glossary", "summary", "answer"];
export const knowledgeTypeSchema = z.enum(KNOWLEDGE_TYPES);
/** Current is served; superseded was replaced by a newer item (supersededBy); retired is gone from its source (the home's own items). */
export const KNOWLEDGE_STATUSES = ["current", "superseded", "retired"];
export const KNOWLEDGE_PRODUCERS = ["workbench", "person", "aioe"];
/** Stable per type: the same key again replaces the item in place (version + 1, the label never lowered). */
export const knowledgeKeySchema = z.string().regex(/^[A-Za-z0-9._:/-]{1,200}$/, "letters, digits and . _ : / -");
/** Where a piece of knowledge came from: the repository, files, commit and session, and the index items it rests on. */
export const knowledgeSourceSchema = z.object({
    rootId: z.string().max(200).optional(),
    files: z.array(z.string().min(1).max(500)).max(50).optional(),
    commit: z.string().max(100).optional(),
    sessionId: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    /** A decision's register number (D215), a pull request, an ADR path: what a person would cite. */
    ref: z.string().max(300).optional(),
    at: z.string().max(40).optional(),
});
/** What a knowledge item's `data` holds (as stored). */
export const knowledgeDataSchema = z.object({
    type: knowledgeTypeSchema,
    key: knowledgeKeySchema,
    title: z.string().min(1).max(300),
    /** Markdown: the decision, the rule, the fact, the steps, the definition, the summary, the answer. */
    text: z.string().max(20_000),
    /** A decision's or convention's reason. */
    why: z.string().max(5_000).optional(),
    /** A glossary entry's other names; any type's words a reader would search by. */
    terms: z.array(z.string().min(1).max(100)).max(30).optional(),
    tags: z.array(z.string().min(1).max(100)).max(30).optional(),
    status: z.enum(KNOWLEDGE_STATUSES).default("current"),
    /** Keys (of the same type) this item replaces: each is marked superseded by it. */
    supersedes: z.array(knowledgeKeySchema).max(20).optional(),
    /** The item id that replaced this one. */
    supersededBy: z.string().max(300).optional(),
    /** Index items (nodes, communities, notes, decisions) it rests on, by id: the graph stays the index. */
    links: z.array(z.string().min(1).max(300)).max(200).default([]),
    sources: z.array(knowledgeSourceSchema).max(20).default([]),
    producer: z.enum(KNOWLEDGE_PRODUCERS),
    /** The model that distilled it, when one did (the workbench's). */
    model: z.string().max(200).optional(),
    confidence: z.number().min(0).max(1).optional(),
    /** When what it says became true (a decision's date); the item's own updatedAt says when it was written. */
    since: z.string().max(40).optional(),
    /** An answer's outcome, from the session that found it. */
    outcome: z.enum(["worked", "corrected", "dead_end"]).optional(),
});
/** One item as a writer sends it. */
export const knowledgeInputSchema = z.object({
    type: knowledgeTypeSchema,
    key: knowledgeKeySchema,
    rootId: z.string().max(200).regex(/^[A-Za-z0-9_.:-]*$/).optional(),
    title: z.string().min(1).max(300),
    text: z.string().max(20_000),
    why: z.string().max(5_000).optional(),
    terms: z.array(z.string().min(1).max(100)).max(30).optional(),
    tags: z.array(z.string().min(1).max(100)).max(30).optional(),
    supersedes: z.array(knowledgeKeySchema).max(20).optional(),
    links: z.array(z.string().min(1).max(300)).max(200).optional(),
    sources: z.array(knowledgeSourceSchema).max(20).optional(),
    model: z.string().max(200).optional(),
    confidence: z.number().min(0).max(1).optional(),
    since: z.string().max(40).optional(),
    /** Raised to the highest label of what it links to; never lower than the item it replaces. */
    label: z.object({ sensitivity: sensitivitySchema.optional(), compartments: z.array(compartmentIdSchema).max(64).default([]) }).optional(),
});
export const KNOWLEDGE_WRITE_MAX = 200;
export const knowledgeWriteSchema = z.object({ items: z.array(knowledgeInputSchema).min(1).max(KNOWLEDGE_WRITE_MAX) });
/** The id a knowledge item is kept under: one per type and key, in its project. */
export const knowledgeItemId = (type, key) => `k:${type}:${key}`;
/** A title as compared for duplicates: lower case, words only, one space. */
export function knowledgeTitleKey(title) {
    return title
        .toLowerCase()
        .replace(/^q:\s*/, "")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
}
/** Where a knowledge type is shown: the six sections of the console's Knowledge view. */
export const KNOWLEDGE_SECTIONS = [
    { id: "decisions", label: "Decisions", types: ["decision"] },
    { id: "conventions", label: "Conventions", types: ["convention"] },
    { id: "architecture", label: "Architecture", types: ["architecture"] },
    { id: "howtos", label: "How-tos", types: ["howto", "answer"] },
    { id: "glossary", label: "Glossary", types: ["glossary"] },
    { id: "summaries", label: "Summaries", types: ["summary"] },
];
//# sourceMappingURL=memoryKnowledge.js.map